import styled from "styled-components";
import { Td, TdCenter } from "./Table.style";
import {
    StandardModalFormGroup,
    StandardModalSection,
    StandardModalSectionTitle,
    StandardModalSelect,
    StandardModalHelperText,
    ScrollableTableContainer
} from "src/components";
import { portalColors } from "src/components";

export const DirectoryTableRow = styled.tr`
    background-color: ${p => p.$isDragging ? "#f0f8ff" : p.$isChildRow ? "#F3F5FB" : "#fff"};
    color: #171A22;
    opacity: ${p => p.$isDragging ? 0.7 : 1};
    box-shadow: ${p => p.$isDragging ? "0 2px 6px rgba(0, 0, 0, 0.2)" : "none"};
    z-index: ${p => p.$isDragging ? 10 : 0};
    position: ${p => p.$isDragging ? "relative" : "static"};
    cursor: grab;
    transition: background-color 0.2s ease;
`;

export const DirectoryTableCell = styled(Td)`
    border-top: ${p => p.$isHovered ? "2px solid blue" : "none"};
    background-color: ${p => p.$isDragging ? "#f0f8ff" : p.$isChildRow ? "#F3F5FB" : "#fff"};
    color: #171A22;
    padding: 20px;
    width: ${p => p.$width || "auto"};
    text-align: left;
    vertical-align: middle;
    word-wrap: ${p => p.$wrap ? "break-word" : "normal"};
    overflow-wrap: ${p => p.$wrap ? "anywhere" : "normal"};
    white-space: ${p => p.$wrap ? "normal" : "nowrap"} !important;
    overflow: ${p => p.$wrap ? "visible" : "hidden"};
    text-overflow: ${p => p.$wrap ? "clip" : "ellipsis"};
`;

export const DirectoryTableCenterCell = styled(TdCenter)`
    border-top: ${p => p.$isHovered ? "2px solid blue" : "none"};
    background-color: ${p => p.$isDragging ? "#f0f8ff" : p.$isChildRow ? "#F3F5FB" : "#fff"};
    color: #171A22;
`;

export const DirectoryNameCellContent = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding-right: 10px;
    width: 100%;
`;

export const DirectoryExpandButton = styled.button`
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);

    width: 18px;
    height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
    line-height: 1;
`;

export const DirectoryExpandIcon = styled.img`
    width: 14px;
    height: 14px;
    transform: ${p => p.$isExpanded ? "rotate(0deg)" : "rotate(-90deg)"};
    transition: transform 0.16s ease;
    filter: brightness(0) saturate(100%);
`;

export const EmptyDirectoryTableCell = styled(Td)`
    text-align: center;
    vertical-align: middle;
    padding-left: 0;
`;

export const EditorSection = styled(StandardModalSection)`
    border-width: 0 0 1px;
    border-radius: 0;
    padding: 1.2rem 1.6rem;
`;

export const HelperText = styled(StandardModalHelperText)`
    color: ${portalColors.mutedText};
    display: block;
    margin: 0;
`;

export const ParentLocationGrid = styled.div`
    display: grid;
    grid-template-columns: ${p => p.$columns};
    gap: 0.85rem;
    align-items: left;

    @media (max-width: 760px) {
        grid-template-columns: 1fr;
    }
`;

export const ParentLocationFormGroup = styled(StandardModalFormGroup)`
    align-items: left;
`;

export const ParentLocationSelect = styled(StandardModalSelect)`
    text-align: left;
    text-align-last: left;
`;

export const WarningText = styled.div`
    color: #FF0000;
    font-size: 0.8125rem;
    font-weight: 600;
`;

export const ParentPickerFilters = styled(ParentLocationGrid)`
    margin-bottom: 0.9rem;
`;

export const ParentPickerTable = styled(ScrollableTableContainer)`
    max-width: none;
    max-height: ${p => p.$hasOverflow ? "16.4rem" : "none"};
    min-height: auto;
    border-radius: 8px;
    border-color: ${portalColors.border};
    box-shadow: ${portalColors.shadow};
    overflow-y: ${p => p.$hasOverflow ? "auto" : "hidden"};
`;

export const ParentPickerRow = styled.div`
    display: grid;
    grid-template-columns: ${p => p.$singleColumn ?
        "3.2rem minmax(0, 1fr)" :
        "3.2rem minmax(0, 1fr) minmax(0, 1fr)"};
    align-items: center;
    min-height: ${p => p.$header ? "2.55rem" : "2.75rem"};
    border-bottom: 1px solid ${portalColors.border};
    background: ${p => p.$header ?
        `linear-gradient(180deg, ${portalColors.surface} 0%, ${portalColors.surfaceSoft} 100%)` :
        portalColors.surface};
    color: ${portalColors.text};
    font-size: ${p => p.$header ? "0.76rem" : "0.8rem"};
    font-weight: ${p => p.$header ? 800 : 600};

    &:last-child {
        border-bottom: 0;
    }
`;

export const ParentPickerCell = styled.div`
    min-width: 0;
    height: 100%;
    display: flex;
    align-items: center;
    padding: 0.65rem 0.75rem;
    border-right: 1px solid ${portalColors.border};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:last-child {
        border-right: 0;
    }
`;

export const ParentPickerHeaderCell = styled(ParentPickerCell)`
    cursor: pointer;
`;

export const ParentPickerHeaderContent = styled.div`
    width: 100%;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
`;

export const ParentPickerHeaderCount = styled.span`
    color: ${portalColors.textSoft};
    font-size: 0.72rem;
    font-weight: 800;
    white-space: nowrap;
`;

export const SelectionSectionTitle = styled(StandardModalSectionTitle)`
    margin-top: 0.4rem;
`;

export const ParentLocationHeaderBlock = styled.div`
    display: grid;
    gap: 0.45rem;
`;

export const VisibilityDisplay = styled.div`
    pointer-events: none;
    opacity: ${p => p.$hasValue ? 1 : 0.72};
`;

export const MixedVisibilityText = styled(HelperText)`
    margin-top: 0.45rem;
`;

export const EmptyParentPicker = styled.div`
    min-height: 8rem;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    color: ${portalColors.mutedText};
    font-size: 0.85rem;
    font-weight: 700;
`;

export const PreviewPath = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.45rem;
`;

export const PreviewPill = styled.span`
    min-width: 4.4rem;
    min-height: 1.85rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.85rem;
    border-radius: 6px;
    background: #0E4F8E;
    color: #FFFFFF;
    font-size: 0.75rem;
    font-weight: 800;
    box-shadow: 0 3px 8px rgba(14, 79, 142, 0.24);
`;

export const PreviewArrow = styled.span`
    width: 1.85rem;
    height: 1.85rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #9FB0D0;
    border-radius: 6px;
    color: #0E4F8E;
    font-size: 1.1rem;
    font-weight: 800;
    background: #FFFFFF;
`;
